import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { CLIENT_TYPE_WEB, CLIENT_TYPE_WECHAT, CLIENT_TYPE_MINIPROGRAM, clientType } from '@/apis/api'

export function isInWechat(): boolean {
  if (import.meta.server) return false
  const ua = navigator.userAgent.toLowerCase()
  return ua.indexOf('micromessenger') != -1
}

export const isIOS = () => {
  if (import.meta.server) return false
  const platform = navigator.platform
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export const _inMiniprograme = ref<boolean>(false)
export const inMiniprograme = _inMiniprograme

export function getWechatClientType(isMiniProgram: boolean): number {
  return isMiniProgram ? CLIENT_TYPE_MINIPROGRAM : CLIENT_TYPE_WECHAT
}

export type WechatEnvType = 'miniProgram' | 'wechatH5' | 'nonWechat'

export async function detectWechatEnvWithLog(scene: string = 'default'): Promise<WechatEnvType> {
  if (typeof window === 'undefined') {
    console.log('[wechat-env] server fallback', { scene, env: 'nonWechat' })
    return 'nonWechat'
  }
  const ua = navigator.userAgent || ''
  const lowerUa = ua.toLowerCase()
  const isWechatUa = lowerUa.includes('micromessenger')
  const hasMiniProgramUaTag = lowerUa.includes('miniprogram') || lowerUa.includes('mini program')
  const wxJsEnvironment = (window as any).__wxjs_environment

  console.log('[wechat-env] detect start', {
    scene,
    isWechatUa,
    hasMiniProgramUaTag,
    wxJsEnvironment
  })

  if (!isWechatUa) {
    console.log('[wechat-env] detect result', {
      scene,
      env: 'nonWechat',
      evidence: 'ua:not_micromessenger'
    })
    return 'nonWechat'
  }

  try {
    const module = await import('weixin-js-sdk')
    const wx = (module as any).default || module
    const hasGetEnv = !!wx?.miniProgram?.getEnv
    console.log('[wechat-env] wx sdk loaded', { scene, hasGetEnv })
    if (hasGetEnv) {
      const res = await new Promise<any>((resolve) => {
        wx.miniProgram.getEnv((envRes: any) => resolve(envRes || {}))
      })
      const isMiniProgram = !!res?.miniprogram
      const env: WechatEnvType = isMiniProgram ? 'miniProgram' : 'wechatH5'
      console.log('[wechat-env] detect result', {
        scene,
        env,
        evidence: 'wx.miniProgram.getEnv',
        getEnvResult: res
      })
      return env
    }
  } catch (error) {
    console.log('[wechat-env] wx sdk load failed', { scene, error })
  }

  if (wxJsEnvironment === 'miniprogram' || hasMiniProgramUaTag) {
    console.log('[wechat-env] detect result', {
      scene,
      env: 'miniProgram',
      evidence: wxJsEnvironment === 'miniprogram' ? '__wxjs_environment' : 'ua:miniprogram'
    })
    return 'miniProgram'
  }

  console.log('[wechat-env] detect result', {
    scene,
    env: 'wechatH5',
    evidence: 'ua:micromessenger'
  })
  return 'wechatH5'
}

export function checkInMiniPrograme() {
  if (typeof window === 'undefined') return
  detectWechatEnvWithLog('checkInMiniPrograme')
    .then((env) => {
      if (env === 'miniProgram') {
        inMiniprograme.value = true
        clientType.value = getWechatClientType(true)
        return
      }
      inMiniprograme.value = false
      if (env === 'wechatH5') {
        clientType.value = getWechatClientType(false)
      } else {
        clientType.value = CLIENT_TYPE_WEB
      }
    })
    .catch((error) => {
      console.log('[wechat-env] detect runtime error', { scene: 'checkInMiniPrograme', error })
      inMiniprograme.value = false
      clientType.value = isInWechat() ? getWechatClientType(false) : CLIENT_TYPE_WEB
    })
}

const { width } = useWindowSize()
export const isMobile = computed(() => width.value < 768)

export function useSsrSafeIsMobile(defaultMobile: boolean = true, breakpoint: number = 768) {
  const hydrated = ref(false)
  const mobile = ref(defaultMobile)

  const syncMobile = () => {
    mobile.value = window.innerWidth < breakpoint
  }

  onMounted(() => {
    hydrated.value = true
    syncMobile()
    window.addEventListener('resize', syncMobile)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', syncMobile)
  })

  return computed(() => (hydrated.value ? mobile.value : defaultMobile))
}

export function getOS() {
  if (import.meta.server) return 'Unknown'
  var userAgent = navigator.userAgent,
    platform = navigator.platform,
    macOS = /Mac OS|Mac/i.test(userAgent),
    windows = /Windows|win64|wow64|win32|win16|wow32/i.test(platform),
    linux = /linux|Linux/i.test(platform)

  if (macOS) {
    return 'Mac'
  } else if (windows) {
    return 'Windows'
  } else if (linux) {
    return 'Linux'
  }
  return 'Unknown'
}

export function isChromeBrowser() {
  if (import.meta.server) return false
  const userAgent = navigator.userAgent
  return (
    userAgent.indexOf('Chrome') > -1 &&
    userAgent.indexOf('Safari') > -1 &&
    userAgent.indexOf('Chromium') === -1 &&
    userAgent.indexOf('Edg') === -1
  )
}

export function isNotMobileBrowser() {
  if (import.meta.server) return true
  const userAgent = navigator.userAgent
  const mobileKeywords = ['Mobile', 'Android', 'iPhone', 'iPad', 'iPod']

  for (let keyword of mobileKeywords) {
    if (userAgent.indexOf(keyword) > -1) {
      return false
    }
  }

  return true
}
